// The API sends every care recipient failure as `{ success: false, message, data: { reason_code, ... } }`.
// rtkBaseQueryWrapper keeps that whole body on the RTK error as `error.data`, so the reason code is read
// from `error.data.data.reason_code` and the HTTP status from `error.status`.

export interface CareRecipientProblem {
  status: number | string | null;
  reasonCode: string | null;
  message: string;
  // Extra fields the API adds for some reason codes (old/new Medicare Ref., client_name).
  data: Record<string, unknown>;
}

const MESSAGE_PREFIX = /^SAH Care Recipients error - /;

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

export const readCareRecipientProblem = (error: unknown): CareRecipientProblem => {
  const err = asRecord(error);
  const body = asRecord(err.data);
  const data = asRecord(body.data);
  const rawMessage =
    (typeof body.message === 'string' && body.message) ||
    (typeof err.message === 'string' && err.message) ||
    'An unknown error occurred';
  return {
    status: typeof err.status === 'number' || typeof err.status === 'string' ? err.status : null,
    reasonCode: typeof data.reason_code === 'string' ? data.reason_code : null,
    message: rawMessage.replace(MESSAGE_PREFIX, ''),
    data,
  };
};

const asText = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value : null);

export interface OverwriteDetails {
  clientName: string | null;
  oldMedicareRef: string | null;
  newMedicareRef: string | null;
}

export const readOverwriteDetails = (problem: CareRecipientProblem): OverwriteDetails => ({
  clientName: asText(problem.data.client_name),
  oldMedicareRef: asText(problem.data.old_medicare_identifier),
  newMedicareRef: asText(problem.data.new_medicare_identifier),
});
